import { useTranslation } from 'react-i18next';

import { useCurrentPartner } from 'services/repositories/partners/partners';
import { useFailedTransactionsCount, useInboxStats } from 'services/repositories/stats/stats';
import { useCurrentUser } from 'services/repositories/user/user';
import { routeToPage } from 'support/helpers/navigation/navigation';
import { InboxRoutes, TransactionsRoutes } from 'support/types';
import { Page } from 'components/Page/Page';

import { Card } from './components/Card';
import { KnockGuideCard } from './components/KnockGuideCard/KnockGuideCard';
import { OCRNudge } from './components/OCRNudge';

export const InboxDashboard = () => {
  const { t } = useTranslation();
  const { data: currentUser, isLoading: isLoadingCurrentUser } = useCurrentUser();
  const { data: currentPartner, isLoading: isLoadingCurrentPartner } = useCurrentPartner();
  const { data: inboxStatsData, isLoading: loadingInboxStatsData } = useInboxStats({
    staleTime: 0,
    refetchOnMount: true,
  });
  const { data: failedTransactionsData, isLoading: loadingFailedTransactions } = useFailedTransactionsCount();

  const isLoading =
    isLoadingCurrentUser || isLoadingCurrentPartner || loadingInboxStatsData || loadingFailedTransactions;

  const hasInboxPendingActions = inboxStatsData?.data
    ? Object.values(inboxStatsData.data).some((count) => (count ?? 0) > 0)
    : false;

  const hasFailedTransactions = (failedTransactionsData?.failedTransactions ?? 0) > 0;

  const hasPendingActions = hasInboxPendingActions || hasFailedTransactions;
  return (
    <Page isLoading={isLoading}>
      <Page.Section>
        <div className="-my-6 mx-auto flex min-h-screen max-w-3xl flex-col gap-8 pb-[40px] pt-12">
          <div className="flex flex-col">
            <Page.Title>{t('dashboard:inbox.title', { name: currentUser?.name })}</Page.Title>
            <Page.Description>
              {hasPendingActions
                ? t('dashboard:inbox.subtitle.pendingActions')
                : t('dashboard:inbox.subtitle.noPendingActions')}
            </Page.Description>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Card
              value={inboxStatsData?.data?.pendingOrderResponses ?? 0}
              title={t('dashboard:inbox.cards.missingOrderResponses')}
              to={routeToPage(InboxRoutes.root, {}, { type: 'pendingOrderResponses' })}
            />
            <Card
              value={inboxStatsData?.data?.pendingShippingNotices ?? 0}
              title={t('dashboard:inbox.cards.missingShippingNotices')}
              to={routeToPage(InboxRoutes.root, {}, { type: 'pendingShippingNotices' })}
            />
            <Card
              value={inboxStatsData?.data?.pendingInvoices ?? 0}
              title={t('dashboard:inbox.cards.missingInvoices')}
              to={routeToPage(InboxRoutes.root, {}, { type: 'pendingInvoices' })}
            />
            <Card
              value={failedTransactionsData?.failedTransactions ?? 0}
              title={t('dashboard:inbox.cards.blockedTransactions')}
              to={TransactionsRoutes.blocked}
            />
          </div>

          {currentPartner?.unattendedOcrEmail ? <OCRNudge email={currentPartner.unattendedOcrEmail} /> : null}

          <div className="flex-1">{/* expander */}</div>
          <KnockGuideCard />
        </div>
      </Page.Section>
    </Page>
  );
};
